// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { EXCHANGE_RATE_ONE } from '@shared/Enums'
import { SierraEventCTO, SierraEventSchema } from './SierraEventCTO'
import { EventNameForTemplate, EventTypeForTemplate } from '@shared/templateApx'
import { SierraReservesCTO, SierraReservesSchema } from './SierraReservesCTO'

export const fetchExchangeRate = async (): Promise<bigint> => {
  return Promise.resolve(EXCHANGE_RATE_ONE + EXCHANGE_RATE_ONE / 10n)
}

export const swapConfirmationFunction = (): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve()
    }, 2000)
  })
}

export const fetchSierraEvents = async (_params: any): Promise<SierraEventCTO[]> => {
  return [
    SierraEventSchema.cast({
      id: '1',
      liquidityTokenSymbol: 'USDC',
      chainId: 1,
      timestamp: Date.now() / 1000,
      blockNumber: 1243,
      eventName: EventNameForTemplate.Swap,
      eventType: EventTypeForTemplate.Swap,
      ageString: '1 hour',
      timestampDateString: '2021-01-01',
      symbol: 'OTMM',
      currency: 'USD',
      poolName: 'OTMM',
      poolAddr: '0x1234567890123456789012345678901234567890',
      fromAddr: '0x1234567890123456789012345678901234567890',
      toAddr: '0x1234567890123456789012345678901234567890',
      assets: '10000000000',
      shares: '10000000000',
      fees: '10000000',
    }),
    SierraEventSchema.cast({
      id: '1',
      liquidityTokenSymbol: 'USDC',
      chainId: 1,
      timestamp: Date.now() / 1000,
      blockNumber: 1243,
      eventName: EventNameForTemplate.Swap,
      eventType: EventTypeForTemplate.Swap,
      ageString: '1 hour',
      timestampDateString: '2021-01-01',
      symbol: 'OTMM',
      currency: 'USD',
      poolName: 'OTMM',
      poolAddr: '0x1234567890123456789012345678901234567890',
      fromAddr: '0x1234567890123456789012345678901234567890',
      toAddr: '0x1234567890123456789012345678901234567890',
      assets: '16000000000',
      shares: '16000000000',
      fees: '10000000',
    }),
    SierraEventSchema.cast({
      id: '1',
      liquidityTokenSymbol: 'USDC',
      chainId: 1,
      timestamp: Date.now() / 1000,
      blockNumber: 1243,
      eventName: EventNameForTemplate.Swap,
      eventType: EventTypeForTemplate.Swap,
      ageString: '2 hours',
      timestampDateString: '2021-01-01',
      symbol: 'OTMM',
      currency: 'USD',
      poolName: 'OTMM',
      poolAddr: '0x1234567890123456789012345678901234567890',
      fromAddr: '0x1234567890123456789012345678901234567890',
      toAddr: '0x1234567890123456789012345678901234567890',
      assets: '14000000000',
      shares: '14000000000',
      fees: '10000000',
    }),
  ]
}

export const fetchSierraReserves = async (_params: any): Promise<SierraReservesCTO[]> => {
  return [
    SierraReservesSchema.cast({
      id: '1',
      poolName: 'OpenTrade MM',
      poolAddr: '0x1234567890123456789012345678901234567890',
      poolToken: 'OTMM',
      apy: 10,
      poolBalance: '10000000000',
      assets: '20000000000',
      percentReserves: 2000,
    }),
    SierraReservesSchema.cast({
      id: '2',
      poolName: 'Corp Bonds',
      poolAddr: '0x1234567890123456789012345678901234567890',
      poolToken: 'OTCB',
      apy: 10,
      poolBalance: '10000000000',
      assets: '30000000000',
      percentReserves: 3000,
    }),
    SierraReservesSchema.cast({
      id: '3',
      poolName: 'High Yield',
      poolAddr: '0x1234567890123456789012345678901234567890',
      poolToken: 'OTHY',
      apy: 10,
      poolBalance: '10000000000',
      assets: '50000000000',
      percentReserves: 5000,
    }),
  ]
}
export interface SierraHistoryCTO {
  timestamp: number
  value: number
}
export enum SierraHistoryPeriod {
  OneMonth = '1M',
  ThreeMonths = '3M',
  SixMonths = '6M',
  OneYear = '1Y',
  All = 'All',
}

export const fetchSierraHistory = async (timerPeriod: SierraHistoryPeriod): Promise<SierraHistoryCTO[]> => {
  const mockData: SierraHistoryCTO[] = []
  const now = Math.floor(Date.now() / 1000)
  let startTime: number
  let interval: number

  switch (timerPeriod) {
    case SierraHistoryPeriod.OneMonth:
      startTime = now - 30 * 24 * 60 * 60 // 30 days ago
      interval = 24 * 60 * 60 // 1 day intervals
      break
    case SierraHistoryPeriod.ThreeMonths:
      startTime = now - 90 * 24 * 60 * 60 // 90 days ago
      interval = 3 * 24 * 60 * 60 // 3 day intervals
      break
    case SierraHistoryPeriod.SixMonths:
      startTime = now - 180 * 24 * 60 * 60 // 180 days ago
      interval = 7 * 24 * 60 * 60 // 7 day intervals
      break
    case SierraHistoryPeriod.OneYear:
      startTime = now - 365 * 24 * 60 * 60 // 365 days ago
      interval = 14 * 24 * 60 * 60 // 14 day intervals
      break
    case SierraHistoryPeriod.All:
      startTime = now - 730 * 24 * 60 * 60 // 2 years ago
      interval = 30 * 24 * 60 * 60 // 30 day intervals
      break
    default:
      startTime = now - 30 * 24 * 60 * 60 // Default to 1 month
      interval = 24 * 60 * 60
  }
  let last = 4.5
  for (let timestamp = startTime; timestamp <= now; timestamp += interval) {
    last = last + Math.random() * 0.1 - 0.02
    mockData.push({
      timestamp,
      value: last, // Random value between 80 and 120
    })
  }
  return mockData
}
